import { Injectable, inject } from '@angular/core';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { Observable, from, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

export interface InfoModalConfig {
  title: string;
  message: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

export interface ConfirmModalConfig {
  title: string;
  message: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  yesText?: string;
  noText?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modalService = inject(MdbModalService);

  /**
   * Apre una modale informativa
   * @param config Configurazione della modale
   * @returns Promise<MdbModalRef>
   */
  async openInfo(config: InfoModalConfig): Promise<MdbModalRef<any>> {
    // Importazione dinamica del componente per evitare dipendenze circolari
    const m = await import('../../shared/components/info-modal/info-modal.component');
    
    const modalRef = this.modalService.open(m.InfoModalComponent, {
      modalClass: `modal-${config.size || 'md'} modal-dialog-centered`,
      data: {
        title: config.title,
        message: config.message,
        showCloseButton: config.showCloseButton ?? true
      }
    });
    
    return modalRef;
  }

  /**
   * Apre una modale di conferma
   * @param config Configurazione della modale
   * @returns Observable<boolean> - true se confermato, false o undefined se annullato
   */
  openConfirm(config: ConfirmModalConfig): Observable<boolean> {
    // Importazione dinamica del componente per evitare dipendenze circolari
    const modalPromise = import('../../shared/components/confirm-modal/confirm-modal.component').then(
      (m) => {
        const modalRef = this.modalService.open(m.ConfirmModalComponent, {
          modalClass: `modal-${config.size || 'md'} modal-dialog-centered`,
          data: {
            title: config.title,
            message: config.message,
            yesText: config.yesText || 'Sì',
            noText: config.noText || 'No'
          }
        });
        return modalRef;
      }
    );

    return from(modalPromise).pipe(
      switchMap((modalRef) => modalRef.onClose),
      map((result) => result === true)
    );
  }
}

